import { useState } from 'react';
import { useReduxState } from 'hooks';
import { getBlockFileForLanguage } from 'lib/courses';
import { FormGroup } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';
import {
  EditorBlockTextField,
  EditorBlockTranslationReplace
} from 'components/editor/blocks';
import { ReaderBlockFileContent } from 'components/reader/blocks/file';

import type { BlockFile } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<EditorStateProps, 'editorLanguageActive'>;

interface UploadSuccessData {
  htmlFile?: string;
  linkType: string;
  url: string;
}

const EditorBlockFile = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockFile> & StateProps) => {
  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const { editorLanguageActive } = stateProps;

  const [showLanguageSpecificUploader, setShowLanguageSpecificUploader] =
    useState(false);

  const { shouldShowDefaultFile, fileUrl } = getBlockFileForLanguage({
    data,
    courseLanguage: course.language,
    editorActiveLanguageVersion: editorLanguageActive,
    showLanguageSpecificUploader
  });

  const handleReplaceCancel = () => {
    setShowLanguageSpecificUploader(false);
  };

  const handleReplaceClick = () => {
    setShowLanguageSpecificUploader(true);
  };

  const handleRemoveLanguageVersionSpecificFile = () => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          file: null
        }
      }
    });
  };

  const handleUploadSuccess = ({
    htmlFile,
    linkType,
    url
  }: UploadSuccessData) => {
    let fileUrl = url;
    if (linkType === 'html' && htmlFile) {
      fileUrl = `${url.slice(0, url.lastIndexOf('/') + 1)}${htmlFile}`.replace(
        '/compressed/',
        '/decompressed/'
      );
    }
    const newData = {
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          file: { url: fileUrl },
          translatable: { ...data.locales[languageVersion]?.translatable }
        }
      }
    };
    updateData(newData);
    setShowLanguageSpecificUploader(false);
  };

  const handleUpdateTitle = (value: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: { title: value }
        }
      }
    });
  };

  const fileData = shouldShowDefaultFile
    ? data.locales[course.language]?.file
    : data.locales[languageVersion]?.file;

  return (
    <div className="file">
      <div className="inner">
        {!fileUrl || showLanguageSpecificUploader ? (
          <div>
            <FileUploader
              askToDecompressZip={false}
              blockType="file"
              disabled={isPreview}
              dragDrop={!isPreview}
              icon="file"
              id={`file-uploader-${blockId}`}
              keyPrefix="uploads/files/"
              onUploadSuccess={handleUploadSuccess}
            />
          </div>
        ) : null}

        {fileUrl && !showLanguageSpecificUploader ? (
          <div className="file-content">
            <ReaderBlockFileContent
              fileUrl={
                shouldShowDefaultFile
                  ? data.locales[course.language]?.file?.url
                  : fileUrl
              }
              title={
                data.locales[languageVersion]?.translatable.title ||
                (fileUrl ? fileUrl.slice(fileUrl.lastIndexOf('/') + 1) : '')
              }
            />
          </div>
        ) : null}

        {editorLanguageActive ? (
          <EditorBlockTranslationReplace
            customStatusText={messages.blocksFileUsingLanguageSpecific}
            defaultStatusText={messages.blocksFileUsingDefaultLanguage}
            downloadableFileUrl={data.locales[languageVersion]?.file?.url}
            hasCustomValue={!shouldShowDefaultFile}
            icon={['far', 'file']}
            onCancel={handleReplaceCancel}
            onRemove={handleRemoveLanguageVersionSpecificFile}
            onReplace={handleReplaceClick}
            showCancel={showLanguageSpecificUploader}
          />
        ) : null}
      </div>

      {!isPreview && fileData && !showLanguageSpecificUploader && (
        <div className="file-attributes">
          <FormGroup label={messages.commonFilename} maxWidth="100%">
            <EditorBlockTextField
              onValueChange={handleUpdateTitle}
              placeholder={
                data.locales[course.language]?.translatable.title ||
                fileData.url.split('/').pop()
              }
              value={data.locales[languageVersion]?.translatable.title || ''}
            />
          </FormGroup>
        </div>
      )}

      <style jsx>{`
        .inner {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .file-attributes {
          margin-top: 15px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockFile;
